/**
 * Writing to the DOM only when the DOM is wrong.
 *
 * Every panel in this game is redrawn from world state on a loop, which is the
 * right shape — a readout that derives itself cannot drift from what it reports.
 * The trap is that the *obvious* way to write one is a lie about cost:
 *
 *     el.textContent = `${Math.floor(w.res.gold)}`;
 *
 * looks like an assignment and is not. `textContent` **discards the existing
 * text node and inserts a new one**, whether or not a single character changed.
 * Sixty frames a second, over a resource strip, a build list, a Power bar and a
 * roster, that is thousands of node removals and insertions per second — for a
 * screen showing exactly the same words it showed on the previous frame.
 *
 * What that costs is not really milliseconds. It is:
 *
 *   • **Flicker.** A subtree that is torn down and rebuilt loses whatever
 *     transient state the browser was holding for it, and repaints.
 *   • **Clicks that do not happen.** A `click` event fires only when the press
 *     and the release land on the *same* element. Replace the element between
 *     the two — which, at 60 Hz, means within sixteen milliseconds of any human
 *     press — and the click is swallowed. Buttons rebuilt every frame are not
 *     buttons; they are pictures of buttons. `hud.ts`'s hero panel and order bar
 *     and `travel.ts`'s door to the realm were all in this state.
 *   • **Scroll position.** A container whose contents are replaced is scrolled
 *     back to the top, so a long panel cannot be read at all. That is the whole
 *     of the Ledger's Powers tab: three and a third thousand pixels of crowns in
 *     a six-hundred-pixel window, thrown back to the top four times a second.
 *
 * So: measure with a `MutationObserver`, and route every periodic write through
 * here. The rule is one line — *compare before you write* — and it is in this
 * file rather than repeated at four dozen call sites so that it cannot be
 * forgotten at the forty-ninth.
 */

/**
 * Set text, if it is not already that text.
 *
 * Reading `textContent` walks the element's descendants to concatenate them, so
 * this is only cheap on the leaf elements it is meant for — a `<span>` holding
 * one number. That is what every caller passes it.
 */
export function setText(el: Element | null | undefined, s: string): void {
  if (!el) return;
  if (el.textContent !== s) el.textContent = s;
}

/**
 * The last markup we wrote to a given element.
 *
 * Kept beside the element rather than read back off it: `innerHTML` is a
 * *serialisation* of the live tree, so comparing against it re-serialises the
 * subtree on every frame — which for a build list is more work than the write it
 * is trying to avoid — and the browser normalises attribute order and quoting on
 * the way out, so the string that comes back is not reliably the string that
 * went in and the comparison never matches.
 *
 * A `WeakMap` because these entries must not keep detached nodes alive: panels
 * here are routinely thrown away wholesale.
 */
const lastHtml = new WeakMap<Element, string>();

/** Set markup, if it is not already that markup. Returns whether it wrote. */
export function setHtml(el: Element | null | undefined, html: string): boolean {
  if (!el) return false;
  if (lastHtml.get(el) === html) return false;
  lastHtml.set(el, html);
  el.innerHTML = html;
  return true;
}

/**
 * Forget what we last wrote to an element.
 *
 * For the case where something *other* than `setHtml` has changed the subtree —
 * a world swap, a screen reopened — and the cache would otherwise report a
 * match against markup that is no longer on screen.
 */
export function forgetHtml(el: Element | null | undefined): void {
  if (el) lastHtml.delete(el);
}

/**
 * Rebuild something, and put the scroll back where the player left it.
 *
 * Replacing the contents of a scrolling box collapses its scroll height to zero
 * for an instant, which the browser answers by clamping `scrollTop` to zero, and
 * it does not restore it when the content comes back. So the position is read
 * first, the rebuild happens, and it is written back — synchronously, in the
 * same task, so nothing is ever painted at the wrong offset.
 *
 * `selectors` names the scrolling descendants to preserve, because the panel
 * being rebuilt is usually not itself the thing that scrolls: the Ledger
 * replaces its whole sheet and the scroller is `.mg-body` inside it. Elements
 * are re-found after the rebuild by the same selector, which is what makes this
 * work across a wholesale replacement — the old node is gone, and its
 * replacement is the one that needs the offset.
 *
 * Only non-zero offsets are restored: writing 0 to a fresh box is a no-op, and
 * skipping it means a genuinely new panel opens at the top rather than at
 * wherever the previous occupant of that selector happened to be.
 */
export function keepScroll(root: Element, selectors: readonly string[], rebuild: () => void): void {
  const was: number[] = [];
  for (const sel of selectors) {
    const el = root.querySelector(sel);
    was.push(el ? el.scrollTop : 0);
  }
  rebuild();
  for (let i = 0; i < selectors.length; i++) {
    if (was[i] <= 0) continue;
    const el = root.querySelector(selectors[i]);
    if (el) el.scrollTop = was[i];
  }
}
